// BadgeCameraScanModal — read a worker badge with the phone camera.
//
// The camera is the path for staff on their OWN phones (the UROVO DT50S uses
// its hardware trigger via useBadgeWedge — which stays live while this modal
// is open, so a trigger pull here still works). A successful decode plays the
// same style of beep the DT50S imager makes, so both paths confirm the same
// way. Decoding goes through
// createBadgeFrameDecoder: the native BarcodeDetector where it exists and can
// read our symbologies (Android Chrome), else a lazy zxing-wasm reader — the
// latter is why this works on iOS Safari, which ships no BarcodeDetector
// (field-confirmed 2026-07-21).
//
// Both symbologies are decoded: QR reads better off a scuffed or curved card,
// Code 128 is what the hardware trigger uses.

import { useEffect, useRef, useState } from 'react'
import { X as CloseIcon, Camera, AlertTriangle, Loader2 } from 'lucide-react'
import { createBadgeFrameDecoder, type BadgeFrameDecoder } from '@/lib/badgeCameraDecode'
import { playScanBeep } from '@/lib/tone'

export default function BadgeCameraScanModal({
  onScanned,
  onClose,
  makeDecoder = createBadgeFrameDecoder,
  heading = 'Scan badge',
  hint = 'Hold the badge steady in the frame',
  noun = 'badge',
}: {
  onScanned: (badgeCode: string) => void
  onClose: () => void
  /** Wave 2e: swap the payload shape (default = worker badges). */
  makeDecoder?: () => Promise<BadgeFrameDecoder>
  heading?: string
  /** The line under the picture; default is for worker badges. */
  hint?: string
  /** What is being scanned, for the start-up error. */
  noun?: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(true)

  // Hold the callbacks in refs so the setup effect can be mount-once. Callers
  // pass a fresh inline `onScanned`/`makeDecoder` every render (e.g. Precool /
  // Tipping), and their parents re-render on a 30s POLL.live tick — with
  // `onScanned` in the dep array, each re-render tore down and re-acquired the
  // camera + decode loop mid-scan, dropping the in-flight decode and flashing
  // "Starting camera…". Mirrors the ref pattern useScanWedge already uses.
  const onScannedRef = useRef(onScanned)
  onScannedRef.current = onScanned
  const makeDecoderRef = useRef(makeDecoder)
  makeDecoderRef.current = makeDecoder

  useEffect(() => {
    let stream: MediaStream | null = null
    let raf = 0
    let stopped = false

    // Justified suppression: a camera-setup effect that is a straight line of
    // fail-closed guards — no camera API, decoder init failed, permission
    // denied, unmounted mid-await — each surfacing an honest error state. The
    // branch count IS that error handling; flattening it would hide failure
    // modes. Not unit-testable (jsdom has no getUserMedia / decoder / camera).
    // fallow-ignore-next-line complexity
    const run = async () => {
      // No camera API at all (insecure context / very old browser).
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This device can’t open the camera. Use the handheld scanner, or type the code printed under the barcode.')
        setStarting(false)
        return
      }

      // Build the decoder first — on iOS this loads the wasm reader (~1 MB), so
      // do it before we ask for the camera to avoid a black preview while it
      // downloads. Fails only if neither backend can init (e.g. offline before
      // the wasm was ever cached).
      let decode
      try {
        decode = await makeDecoderRef.current()
      } catch {
        setError(`Couldn’t start the ${noun} scanner on this device. Use the handheld scanner, or type the code printed under the barcode.`)
        setStarting(false)
        return
      }
      if (stopped) return

      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      } catch {
        setError('Could not open the camera. Check the permission and try again.')
        setStarting(false)
        return
      }
      if (stopped) { stream.getTracks().forEach(t => t.stop()); return }

      const video = videoRef.current
      if (video) {
        video.srcObject = stream
        void video.play()
      }
      setStarting(false)

      // Justified suppression: a requestAnimationFrame decode loop whose
      // branches are the loop lifecycle (stopped / no frame yet / hit). Not
      // unit-testable — jsdom has no getUserMedia, no real rAF, and no camera
      // frames; a test would assert against mocks of all three. The decode step
      // that CAN be tested is firstBadgeFromTexts(). Real proof is a device scan.
      // fallow-ignore-next-line complexity
      const tick = async () => {
        const v = videoRef.current
        if (!stopped && v && v.readyState >= 2) {
          const code = await decode(v)
          if (code) { stopped = true; playScanBeep(); onScannedRef.current(code); return }
        }
        if (!stopped) raf = requestAnimationFrame(() => { void tick() })
      }
      void tick()
    }

    void run()

    return () => {
      stopped = true
      cancelAnimationFrame(raf)
      stream?.getTracks().forEach(t => t.stop())
    }
    // Mount-once: callbacks are read through refs so a new inline `onScanned`
    // identity on a parent re-render never re-runs this camera-setup effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div data-theme="dark" className="fixed inset-0 z-[60] bg-black/90 flex flex-col">
      <header className="px-4 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)] flex items-center gap-3 shrink-0">
        <span className="text-white font-semibold flex-1">{heading}</span>
        <button onClick={onClose} aria-label="Close"
          className="text-fg-secondary hover:text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
          <CloseIcon size={22} />
        </button>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-4 gap-4">
        {error ? (
          <div className="max-w-sm text-center space-y-3">
            <AlertTriangle size={32} className="text-red-400 mx-auto" />
            <p className="text-sm text-gray-200">{error}</p>
          </div>
        ) : (
          <>
            <div className="relative w-full max-w-md">
              <video ref={videoRef} playsInline muted
                className="w-full rounded-xl bg-black aspect-[4/3] object-cover" />
              {starting && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-fg-secondary">
                  <Loader2 size={24} className="animate-spin" />
                  <span className="text-xs">Starting camera…</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <Camera size={14} /> {hint}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
